import { formatCount } from "@/lib/format-count";
// src/features/statistics/chart-primitives.tsx
import { Info } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

/**
 * IRIS neon chart palette — each entry is an SVG-safe hex that maps to an iris token.
 * CSS custom properties don't work reliably in SVG stroke/fill attributes,
 * so we use literal hex values here with comments documenting the token origin.
 */
const CHART_COLORS_TUPLE = [
  "#ff2ea6", // --iris-neon-magenta // token-exempt
  "#a855f7", // --iris-neon-purple // token-exempt
  "#39ff88", // --iris-neon-green // token-exempt
  "#22d3ff", // --iris-neon-cyan // token-exempt
  "#ff9933", // --iris-neon-orange // token-exempt
  "#3e8dff", // --iris-neon-blue // token-exempt
  "#ff4dd2", // --iris-neon-pink // token-exempt
  "#eaff2a", // --iris-neon-yellow // token-exempt
  "#ff3355", // --iris-neon-red // token-exempt
] as const;

/** Safe color lookup — wraps around the palette. */
export function chartColor(index: number): string {
  return CHART_COLORS_TUPLE[index % CHART_COLORS_TUPLE.length]!;
}

/**
 * Semantic chart color map — use these for specific chart intents
 * so every chart across Analytics uses a consistent visual language.
 */
export const CHART_SEMANTIC = {
  /** Pie/donut dominant pair */
  piePrimary: "#ff2ea6", // --iris-neon-magenta // token-exempt
  pieSecondary: "#a855f7", // --iris-neon-purple // token-exempt
  /** OK / active series */
  ok: "#39ff88", // --iris-neon-green // token-exempt
  /** Info / DEV series */
  info: "#22d3ff", // --iris-neon-cyan // token-exempt
  /** WARN series */
  warn: "#ff9933", // --iris-neon-orange // token-exempt
  /** Risk bars */
  risk: "#3e8dff", // --iris-neon-blue // token-exempt
} as const;

/** Gridline stroke — matches design spec: rgba(255,255,255,0.04) */
const GRID_STROKE = "rgba(255,255,255,0.04)";

/** Axis tick fill — matches --iris-fg-4 token */
const AXIS_TICK_FILL = "#6b7184"; // --iris-fg-4 // token-exempt

const TOOLTIP_STYLE = {
  background: "var(--iris-bg-3)",
  border: "1px solid var(--iris-line-2)",
  borderRadius: "var(--iris-r-sm)",
  fontSize: "var(--iris-t-small)",
  color: "var(--iris-fg-2)",
} as const;

/**
 * Collapsible panel — the shared card chrome for every Analytics section.
 *
 * Adopts the IRIS "Statistics screen" design (IR-1046 ④): each panel carries an
 * optional info-ⓘ tooltip and a chevron toggle that collapses the body, on a
 * clean dark `iris-card`. Default expanded. The caller supplies the body's fixed
 * height so charts size consistently whether they're 256 (distribution) or 192
 * (activity) tall.
 */
export function CollapsibleCard({
  title,
  subtitle,
  info,
  defaultOpen = true,
  children,
}: {
  title: string;
  subtitle?: string | undefined;
  info?: string | undefined;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="iris-card" style={{ padding: 20 }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h3 className="iris-h3" style={{ fontSize: 14, margin: 0 }}>
            {title}
          </h3>
          {subtitle && (
            <p className="iris-meta" style={{ marginTop: 2, marginBottom: 0 }}>
              {subtitle}
            </p>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
          {info && (
            <span
              title={info}
              aria-label={info}
              style={{
                display: "grid",
                placeItems: "center",
                color: "var(--iris-neon-cyan)",
                cursor: "help",
              }}
            >
              <Info size={14} />
            </span>
          )}
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? `Collapse ${title}` : `Expand ${title}`}
            aria-expanded={open}
            style={{
              display: "grid",
              placeItems: "center",
              background: "transparent",
              border: "none",
              color: "var(--iris-fg-4)",
              cursor: "pointer",
              padding: 2,
            }}
          >
            {/* Chevron points down when open, right (-90°) when collapsed */}
            <svg
              width={14}
              height={14}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              role="presentation"
              style={{
                transform: open ? "none" : "rotate(-90deg)",
                transition: "transform 150ms ease",
              }}
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>
        </div>
      </div>
      {open && <div style={{ marginTop: 12 }}>{children}</div>}
    </div>
  );
}

/**
 * Distribution chart card. Thin wrapper over {@link CollapsibleCard} that fixes
 * the body to the standard 256px chart height so every breakdown card lines up.
 */
export function ChartCard({
  title,
  subtitle,
  info,
  children,
}: {
  title: string;
  subtitle?: string | undefined;
  info?: string | undefined;
  children: ReactNode;
}) {
  return (
    <CollapsibleCard title={title} subtitle={subtitle} info={info}>
      <div style={{ height: 256 }}>{children}</div>
    </CollapsibleCard>
  );
}

// biome-ignore lint/suspicious/noExplicitAny: recharts data is inherently loosely typed
type ChartData = Record<string, any>[];

/**
 * Collapse a long-tailed category list into the top-N slices plus a single
 * aggregated "Other" bucket. Without this, a breakdown that returns hundreds of
 * freeform categories (e.g. entity "By Platform" with unnormalized values)
 * makes the pie's legend and slice labels explode across the entire page.
 */
export function collapseToTopSlices(
  data: ChartData,
  nameKey: string,
  dataKey: string,
  maxSlices: number,
): ChartData {
  if (!Array.isArray(data) || data.length <= maxSlices) return data;
  const sorted = [...data].sort((a, b) => (Number(b[dataKey]) || 0) - (Number(a[dataKey]) || 0));
  const head = sorted.slice(0, maxSlices - 1);
  const tail = sorted.slice(maxSlices - 1);
  const otherCount = tail.reduce((sum, r) => sum + (Number(r[dataKey]) || 0), 0);
  return [...head, { [nameKey]: `Other (${tail.length})`, [dataKey]: otherCount }];
}

/**
 * Outer pie-slice label, suppressed for thin slices.
 *
 * Retained for any legacy pie usage. The Analytics distribution charts now use
 * {@link DonutWithLegend} (counts live in the side legend), so this band-aid is
 * no longer on the critical path — but it stays exported + tested.
 */
export function pieSliceLabel(
  name: string | number | undefined,
  percent: number | undefined,
  minPercent = 0.05,
): string {
  const pct = percent ?? 0;
  if (pct < minPercent) return "";
  return `${String(name ?? "")} ${(pct * 100).toFixed(0)}%`;
}

/**
 * IR-1386 r4 (Rotem: "tooltip doesnt work") — the two layers that overlap in a
 * donut, as ONE ordered pair rather than two numbers set in different places.
 *
 * The centre-total overlay is a positioned sibling that comes AFTER the chart in
 * the DOM, so at z-index auto it paints over recharts' tooltip: hovering a slice
 * put the slice's name and count *underneath* the big number, on a 116px ring
 * where the tooltip opens right across it. Hover itself was never broken — the
 * overlay is `pointerEvents: none`.
 *
 * Keep them as constants so the invariant below (tooltip above label) is what a
 * future change has to break deliberately, rather than by re-touching a literal.
 */
export const DONUT_CENTER_LABEL_Z = 1;
export const DONUT_TOOLTIP_Z = 30;

export interface DonutSlice {
  name: string;
  value: number;
  color: string;
  percent: number;
}

/**
 * Per-slice color override, keyed on the slice NAME (IR-1351 r9).
 *
 * The default `chartColor(index)` palette is deliberately meaning-free: it
 * exists to keep N arbitrary slices telling apart. That is the wrong tool when
 * the slices are STATUSES an operator already reads elsewhere in a fixed color
 * vocabulary — a Connected slice drawn magenta and a Disconnected one drawn
 * purple carry proportion but no meaning, so the reader still has to consult the
 * legend for the one thing the chart was added to show at a glance.
 *
 * Returning `undefined` for a name falls back to the palette, so a caller can
 * color the slices it has an opinion about and leave the rest alone.
 */
export type SliceColorFn = (name: string, index: number) => string | undefined;

/**
 * Pure shaping for {@link DonutWithLegend}: collapse the long tail, then attach a
 * palette color + share-of-total percent to each slice and return the grand total.
 * Kept side-effect-free so it can be unit-tested without rendering recharts.
 */
export function donutSlices(
  data: ChartData,
  nameKey: string,
  dataKey: string,
  maxSlices = 8,
  colorFor?: SliceColorFn,
): { slices: DonutSlice[]; total: number } {
  const collapsed = collapseToTopSlices(data, nameKey, dataKey, maxSlices);
  const rows = Array.isArray(collapsed) ? collapsed : [];
  const total = rows.reduce((sum, r) => sum + (Number(r[dataKey]) || 0), 0);
  const slices = rows.map((r, i) => {
    const value = Number(r[dataKey]) || 0;
    const name = String(r[nameKey] ?? "");
    return {
      name,
      value,
      // A caller that knows what its slices MEAN can override the palette.
      // Falling back to `chartColor(i)` keeps every existing donut byte-identical.
      color: colorFor?.(name, i) ?? chartColor(i),
      percent: total > 0 ? value / total : 0,
    };
  });
  return { slices, total };
}

/**
 * Donut chart with a center total and a right-side legend listing each slice's
 * exact count — the IRIS "Raw Materials" pattern (IR-1046 ④). Replaces the old
 * outer-label pie, whose thin-slice labels stacked into an unreadable smear.
 */
export function DonutWithLegend({
  data,
  nameKey,
  dataKey,
  maxSlices = 8,
  totalLabel = "Total",
  colorFor,
  size = 160,
  centerValue,
}: {
  data: ChartData;
  nameKey: string;
  dataKey: string;
  maxSlices?: number;
  totalLabel?: string;
  colorFor?: SliceColorFn;
  size?: number;
  /**
   * IR-1482 r2: override the centre number.
   *
   * The centre is normally the sum of the slices, which is right whenever the
   * ring charts exactly the population its centre names — the Avatars-tab
   * rings, where the slices ARE the connected avatars. The Groups rings chart
   * every group but are asked to headline the CONNECTED count (Rotem: "I want
   * 'Connected by collection', connected by ingestion - NOT ALL"), so there the
   * centre is a labelled subset of the ring rather than its total.
   *
   * Only honoured when provided, so every existing donut is unchanged. The
   * centre carries its own label (`totalLabel`), which is what keeps a subset
   * from reading as a total.
   */
  centerValue?: number | undefined;
}) {
  const { slices, total } = useMemo(
    () => donutSlices(data, nameKey, dataKey, maxSlices, colorFor),
    [data, nameKey, dataKey, maxSlices, colorFor],
  );
  // `?? total` rather than `||`: an explicit centre of 0 is a real answer
  // ("nothing is connected"), not a missing prop.
  const centre = centerValue ?? total;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16, height: "100%" }}>
      <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              // Proportional to `size` so a smaller donut keeps the same ring
              // weight rather than collapsing to a disc (the 160px default
              // reproduces the original 52/74 exactly).
              innerRadius={size * 0.325}
              outerRadius={size * 0.4625}
              paddingAngle={1}
              stroke="none"
            >
              {slices.map((s, i) => (
                <Cell key={`cell-${String(i)}`} fill={s.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(value, name) => [(Number(value) || 0).toLocaleString(), name]}
              // IR-1386 r4 (Rotem: "tooltip doesnt work") — see
              // DONUT_TOOLTIP_Z. The tooltip has to outrank the centre-total
              // overlay, which the DOM order otherwise gives the win to.
              wrapperStyle={{ zIndex: DONUT_TOOLTIP_Z }}
            />
          </PieChart>
        </ResponsiveContainer>
        {/* Center total — overlay so it survives any recharts label quirks */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            pointerEvents: "none",
            // IR-1386 r4: an explicit rank, so the tooltip's is comparable to
            // it instead of both relying on DOM order.
            zIndex: DONUT_CENTER_LABEL_Z,
          }}
          data-testid="donut-center-label"
        >
          <div style={{ textAlign: "center" }}>
            <div className="iris-meta" style={{ fontSize: 11 }}>
              {totalLabel}
            </div>
            <div
              // IR-1046 ⑤: compact "as done in IRIS" (1.1M); exact on hover.
              title={centre.toLocaleString()}
              style={{
                fontSize: 24,
                fontWeight: 600,
                color: "var(--iris-fg-1)",
                lineHeight: 1.1,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {formatCount(centre)}
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          maxHeight: "100%",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        {slices.map((s) => (
          <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
            <span
              style={{
                width: 9,
                height: 9,
                borderRadius: 2,
                background: s.color,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                color: "var(--iris-fg-3)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
            >
              {s.name}
            </span>
            <span
              // IR-1046 ⑤: compact value; exact count on hover.
              title={s.value.toLocaleString()}
              style={{
                color: "var(--iris-fg-1)",
                fontWeight: 500,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {formatCount(s.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SimplePie({
  data,
  nameKey,
  dataKey,
  innerRadius,
  maxSlices = 8,
}: {
  data: ChartData;
  nameKey: string;
  dataKey: string;
  innerRadius?: number;
  maxSlices?: number;
}) {
  const pieRadiusProps =
    innerRadius != null ? { innerRadius, outerRadius: 80 } : { outerRadius: 80 };

  const chartData = useMemo(
    () => collapseToTopSlices(data, nameKey, dataKey, maxSlices),
    [data, nameKey, dataKey, maxSlices],
  );

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={chartData}
          dataKey={dataKey}
          nameKey={nameKey}
          cx="50%"
          cy="50%"
          {...pieRadiusProps}
          label={({
            name,
            percent,
          }: {
            name?: string | number;
            percent?: number;
          }) => pieSliceLabel(name, percent)}
          labelLine={false}
          fontSize={11}
          /* --iris-fg-2 for label text */
          fill="#e6e8ef" /* token-exempt: SVG fill */
        >
          {chartData.map((_, i) => (
            <Cell key={`cell-${String(i)}`} fill={chartColor(i)} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          formatter={(value) => [(Number(value) || 0).toLocaleString(), "count"]}
        />
        <Legend
          wrapperStyle={{ fontSize: 11 }}
          formatter={(value: string) => <span style={{ color: "var(--iris-fg-3)" }}>{value}</span>}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function SimpleBar({
  data,
  xKey,
  yKey,
  color,
  layout,
  leftMargin,
  tickFontSize,
}: {
  data: ChartData;
  xKey: string;
  yKey: string;
  color?: string;
  layout?: "horizontal" | "vertical";
  leftMargin?: number;
  tickFontSize?: number;
}) {
  const isVertical = layout === "vertical";
  const fontSize = tickFontSize ?? 11;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        layout={isVertical ? "vertical" : "horizontal"}
        margin={{ top: 5, right: 20, bottom: 5, left: leftMargin ?? (isVertical ? 80 : 5) }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
        {isVertical ? (
          <>
            {/* IR-1046 ⑤: compact axis ticks (25K/100K) "as done in IRIS". */}
            <XAxis
              type="number"
              tick={{ fontSize, fill: AXIS_TICK_FILL }}
              tickFormatter={(v: number) => formatCount(v)}
            />
            <YAxis
              type="category"
              dataKey={xKey}
              tick={{ fontSize, fill: AXIS_TICK_FILL }}
              width={leftMargin ? leftMargin - 10 : 70}
            />
          </>
        ) : (
          <>
            <XAxis dataKey={xKey} tick={{ fontSize, fill: AXIS_TICK_FILL }} />
            <YAxis
              tick={{ fontSize, fill: AXIS_TICK_FILL }}
              tickFormatter={(v: number) => formatCount(v)}
            />
          </>
        )}
        {/* Exact count on hover (tooltip keeps full precision). */}
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          formatter={(value) => [(Number(value) || 0).toLocaleString(), yKey]}
        />
        <Bar dataKey={yKey} fill={color ?? chartColor(0)} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
